import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SectionHeading, MandirCard, TestimonialsSection } from "@/components/Cards";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, BadgeCheck, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1726501604891-19fb7f7cd37b?crop=entropy&cs=srgb&fm=jpg&q=85";
const CTA_IMG = "https://images.unsplash.com/photo-1605302977545-3a09913be1dd?crop=entropy&cs=srgb&fm=jpg&q=85";

const HIGHLIGHTS = [
  { title: "Authentic rituals", text: "Every worship is performed by learned priests following Vedic scriptures without compromise." },
  { title: "Trusted mandirs", text: "We partner only with revered temples known for their spiritual power and integrity." },
];

export default function Mandirs() {
  const [mandirs, setMandirs] = useState([]);
  const [q, setQ] = useState("");
  const [stats, setStats] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/mandirs").then((r) => setMandirs(r.data));
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
  }, []);

  const filtered = mandirs.filter((m) => {
    const s = q.toLowerCase();
    return !s || m.name.toLowerCase().includes(s) || m.city.toLowerCase().includes(s) || m.deity.toLowerCase().includes(s);
  });

  return (
    <div>
      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="animate-float-up">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-accent mb-3">Blessings</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight leading-[1.08]">
              Why devotees choose Pavitra Mantra for sacred connection
            </h1>
            <p className="mt-5 text-muted-foreground text-lg leading-relaxed max-w-xl">
              We bring the ancient traditions of Hindu worship to your fingertips. No distance is too great for sincere devotion.
            </p>
            <div className="grid sm:grid-cols-2 gap-6 mt-8">
              {HIGHLIGHTS.map((h) => (
                <div key={h.title} data-testid={`hero-highlight-${h.title.split(" ")[0].toLowerCase()}`}>
                  <h3 className="font-serif text-lg font-semibold flex items-center gap-2"><BadgeCheck className="h-5 w-5 text-accent" /> {h.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{h.text}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-9">
              <a href="#featured-mandirs"><Button data-testid="hero-explore-btn" variant="outline" className="rounded-full h-11 px-7 border-accent text-foreground hover:bg-accent hover:text-accent-foreground">Explore</Button></a>
              <Link to="/divine-bless" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all" data-testid="hero-book-link">Book Worship <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
          <div className="animate-float-up" style={{ animationDelay: "0.1s" }}>
            <div className="relative rounded-3xl overflow-hidden border border-border shadow-sm">
              <img src={HERO} alt="Sacred Mandir" className="w-full h-[300px] lg:h-[460px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Featured (with search) */}
      <section id="featured-mandirs" className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pb-14 lg:pb-20 scroll-mt-24">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <SectionHeading eyebrow="Featured" title={`${filtered.length} Mandirs available`} subtitle="Choose a temple to view its history, deity, timings and available worships." />
          <div className="relative w-full lg:max-w-sm">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input data-testid="mandir-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by Mandir or city…" className="pl-12 rounded-full bg-card border-border h-12 text-base" />
          </div>
        </div>
        {filtered.length === 0 ? (
          <p className="text-muted-foreground" data-testid="mandir-empty">No mandirs match "{q}". Try a different search.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="mandir-grid">
            {filtered.map((m) => <MandirCard key={m.id} m={m} />)}
          </div>
        )}
      </section>

      {/* Stats */}
      {stats && (
        <section className="bg-secondary/40 border-y border-border">
          <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-16">
            <SectionHeading center eyebrow="Devotion, Quantified" title="Faith, measured in seva"
              subtitle="Behind every number is a devotee whose prayers reached their chosen Mandir. Here is the trust the community has placed in Pavitra Mantra." />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center mt-4">
              {[["Mandirs", stats.mandirs], ["Worships & Rituals", stats.services], ["Verified Purohits", stats.purohits], ["Bookings Served", (stats.bookings/1000).toFixed(0) + "K+"]].map(([l, v]) => (
                <div key={l}><p className="font-serif text-4xl font-bold text-primary">{v}</p><p className="text-sm text-muted-foreground mt-1">{l}</p></div>
              ))}
            </div>
          </div>
        </section>
      )}

      <TestimonialsSection testimonials={testimonials} />

      {/* Book Worship CTA */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 lg:py-20">
        <div className="relative rounded-3xl overflow-hidden p-8 sm:p-12 flex flex-col md:flex-row items-center justify-between gap-6 min-h-[240px] border border-border">
          <img src={CTA_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/35" />
          <div className="relative text-white">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold">Ready to offer your worship?</h2>
            <p className="mt-2 text-white/90 max-w-lg">Choose a worship or Vedic ritual and let a verified Purohit perform it at your chosen Mandir.</p>
          </div>
          <Link to="/divine-bless" className="relative"><Button data-testid="mandirs-book-cta" className="rounded-full h-12 px-7 bg-white text-primary hover:bg-white/90">Book Worship <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
        </div>
      </section>
    </div>
  );
}
